import React, { useState } from 'react';
import { fetchRelatedPartiesData } from '../../../../store/dashboard/relatedParties/relatedParties_apiHelper';
import styles from './RelatedParties.module.scss';
import { caretRightPurple, infoIconGray } from '../../../../assets/hostedassets';
import Tooltip from '../../../../common/_custom/Tooltip/Tooltip';
import SideDrawer from '../../../../common/_custom/SideDrawer/SideDrawer';
import RelatedPartiesAlerts from './RelatedPartiesAlerts/RelatedPartiesAlerts';
import clsx from 'clsx';
import { useParams } from 'react-router-dom';
import { formatDateByFormat, DATE_FORMATS } from '../../../../utils/dateUtils';
import { AION_INGEST_RELATED_PARTIES_DATA, getEdgeTypeLabels, getRelatedPartyHeaders } from './relatedParty_constants';
import A4Page from '../../../AicaPDFReport/A4Page/A4Page';
import { REPORT_SECTION_NAMES } from '../../../../store/pdfReport/pdfReport_constants';
import FirstDegreeRow from './components/FirstDegreeRow/FirstDegreeRow';
import SecondDegreeRow from './components/SecondDegreeRow/SecondDegreeRow';
import { useRelatedPartiesDataFetch } from './useRelatedPartiesDataFetch';
import { useRelatedPartyPolling } from './useRelatedPartiesPolling';
import AicaOverview from '../../../../common/AicaOverview/AicaOverview';
import AskAicaQuestionBadge from '../../../../common/AskAicaQuestionBadge/AskAicaQuestionBadge';
import { useAppSelector } from '../../../../app/hooks';
import { TAB_TO_TASK_TRACKER_MAP } from '../../../../utils/constants/commonConstants';

function RelatedParties({ isPDFReport = false }) {
    const params = useParams();
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState('');
    const [isProbeEnabled, setIsProbeEnabled] = useState(false);
    const taskTrackerMap = useAppSelector((state) => state.investor.taskTrackerMap);

    const { startPolling, stopPolling } = useRelatedPartyPolling(fetchRelatedParties, setStatus, setLoading);

    const {
        // loading,
        expandAll,
        // status,
        showAlerts,
        relatedPartyData,
        relatedPartyGroupedData,

        //FUNCTIONS
        // setLoading,
        // setStatus,
        isDataEmpty,
        setRelatedPartyData,
        handleAlertsClick,
        handleShowRelationsClick,
        toggleExpandCollapseAll,
        setShowAlerts,
    } = useRelatedPartiesDataFetch(isPDFReport, fetchRelatedParties);

    function fetchRelatedParties(isPollingCall = false) {
        if (!isPollingCall) setLoading(true);

        fetchRelatedPartiesData({
            orgId: params.id,
            taskStepsList: [AION_INGEST_RELATED_PARTIES_DATA],
        })
            .then((data) => {
                const relatedPartiesData = data?.['UW_FETCH_RELATED_PARTIES'];
                const taskTrackerData = data?.['AICA_GET_TASK_TRACKER_DATA']?.taskTrackerData;
                const probeConfig = data?.['FETCH_RELATED_PARTY_CONFIG_POLUS'];

                if (relatedPartiesData?.data) {
                    const taskTrackerCreateDate = taskTrackerMap?.[TAB_TO_TASK_TRACKER_MAP.relatedParty[0]]?.dateCreated;
                    setRelatedPartyData({ ...relatedPartiesData, taskTrackerCreateDate });
                }

                if (probeConfig?.isProbeEnabled !== undefined) {
                    setIsProbeEnabled(probeConfig.isProbeEnabled);
                }

                if (
                    !(AION_INGEST_RELATED_PARTIES_DATA in taskTrackerData) ||
                    ['TODO', 'TODO_ASYNC'].includes(taskTrackerData?.[AION_INGEST_RELATED_PARTIES_DATA])
                ) {
                    setStatus('PROCESSING');
                    startPolling();
                } else {
                    setStatus('COMPLETE');
                    stopPolling();
                }
            })
            .catch((err) => {})
            .finally(() => {
                setLoading(false);
            });
    }
    const renderHeaders = () => (
        <tr className={styles.Headers}>
            {getRelatedPartyHeaders(isPDFReport).map((header, i) => (
                <th
                    className={clsx({
                        [styles.Header_FromName]: header.key === 'relatedPartyName' || header.key === 'relationTo',
                        [styles.Header_ToName]: header.key === 'relationTo',
                        [styles.Header_Relation]: header.key === 'relation',
                        [styles.Header_Risk]: header.key === 'risk',
                        [styles.Header_Alerts]: header.key === 'alerts',
                    })}
                    key={header.key}
                >
                    <div className={clsx(styles.Header)}>
                        {header.label}
                        {header.tooltip && !isPDFReport && (
                            <Tooltip content={header.tooltip} direction="bottom" style={{ width: '250px' }}>
                                <img
                                    src={infoIconGray}
                                    alt="info"
                                    className={styles.TooltipIcon}
                                    style={{
                                        marginLeft: '5px',
                                        marginTop: '4px',
                                        transform: 'rotate(180deg)',
                                    }}
                                    height={'15px'}
                                />
                            </Tooltip>
                        )}

                        {header.key === 'relatedPartyName' && !isPDFReport && (
                            <div className={styles.ExpandButton} onClick={toggleExpandCollapseAll}>
                                {expandAll ? 'Collapse All' : 'Expand All'}
                                <img
                                    className={styles.ExpandIcon}
                                    src={caretRightPurple}
                                    alt={''}
                                    height={'10px'}
                                    style={{
                                        transform: expandAll ? 'rotate(270deg)' : '',
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </th>
            ))}
        </tr>
    );

    const render = (
        <div
            className={clsx(styles.RelatedParties, {
                [styles.RelatedPartiesPDFReport]: isPDFReport,
            })}
        >
            <div className={styles.PageHeader}>
                <div className={styles.PageHeaderLeft}>
                    <div className={styles.PageTitleGroup}>
                        <div className={styles.PageTitle} style={{ fontSize: isPDFReport ? '1rem' : '' }}>
                            Structure & Relations
                        </div>
                        {!isPDFReport && (
                            <Tooltip
                                content="View how this entity is connected to directors, shareholders, and other parties"
                                direction="bottom"
                                style={{ width: '260px' }}
                            >
                                <img src={infoIconGray} alt="info" className={styles.PageTitleInfo} height="18px" width="18px" />
                            </Tooltip>
                        )}
                    </div>
                </div>
            </div>

            <div className={styles.SectionCard}>
                <div className={styles.SectionHeader}>
                    <div className={styles.SectionTitle}>Related Party</div>
                    <div className={styles.StatusRight}>
                        {!isPDFReport && !isDataEmpty() && (
                            <AskAicaQuestionBadge
                                question="Any related-party risks?"
                                prompt="Summarize the related-party data and highlight any potential risks"
                            />
                        )}
                        {isDataEmpty() ? (
                            <>
                                <span className={styles.NoMcaData}>No Data Available on MCA</span>
                                {relatedPartyData?.taskTrackerCreateDate && (
                                    <span className={styles.emptyDate}>
                                        Last updated on{' '}
                                        {formatDateByFormat(relatedPartyData?.taskTrackerCreateDate, DATE_FORMATS.DATE_FORMAT_DD_MMM_YYYY)}
                                    </span>
                                )}
                            </>
                        ) : (
                            relatedPartyData?.lastUpdatedAt && (
                                <span className={styles.LastUpdated}>
                                    Last updated on{' '}
                                    {formatDateByFormat(relatedPartyData?.lastUpdatedAt, DATE_FORMATS.DATE_FORMAT_DD_MMM_YYYY)}
                                </span>
                            )
                        )}
                    </div>
                </div>
                {isDataEmpty() ? null : (
                    <div className={styles.Body}>
                        <AicaOverview style={{ marginBottom: '1rem' }} module={'relatedParty'} isPDFReport={!!isPDFReport} />
                        <div className={styles.TableContainer}>
                            <table className={styles.Table3}>
                                <thead>{renderHeaders()}</thead>
                                <tbody>
                                    {Object.entries(relatedPartyGroupedData).map(([edgeType, data]: [string, any], i) => {
                                        const filteredData = Object.entries(data).filter(([, entityEntries]: [string, any]) => {
                                            const entry = entityEntries[0];
                                            return entry?.fromName && entry?.fromName !== 'null';
                                        });
                                        if (!filteredData.length) return null;
                                        return (
                                            <React.Fragment key={edgeType}>
                                                <tr>
                                                    <td colSpan={isPDFReport ? 4 : 5} className={styles.EdgeGroupType}>
                                                        {getEdgeTypeLabels(edgeType)} ({filteredData.length})
                                                    </td>
                                                </tr>
                                                {filteredData.map(([fromKid, entityEntries]: [string, any], i) => {
                                                    const entityEntry = entityEntries[0];
                                                    const showSecondDegreeRow = !!(
                                                        (expandAll || entityEntry?.isExpanded) &&
                                                        entityEntry?.rows?.length
                                                    );
                                                    return entityEntry ? (
                                                        <React.Fragment key={fromKid}>
                                                            <FirstDegreeRow
                                                                edgeType={edgeType}
                                                                isPDFReport={isPDFReport}
                                                                expandAll={expandAll}
                                                                entityEntry={entityEntry}
                                                                handleAlertsClick={handleAlertsClick}
                                                                handleShowRelationsClick={handleShowRelationsClick}
                                                                isProbeEnabled={isProbeEnabled}
                                                            />
                                                            {showSecondDegreeRow &&
                                                                entityEntry?.rows?.map((row, j) =>
                                                                    row?.fromName && row?.fromName !== 'null' ? (
                                                                        <SecondDegreeRow
                                                                            key={j}
                                                                            isPDFReport={isPDFReport}
                                                                            entityEntry={entityEntry}
                                                                            row={row}
                                                                            handleAlertsClick={handleAlertsClick}
                                                                            isProbeEnabled={isProbeEnabled}
                                                                        />
                                                                    ) : (
                                                                        <React.Fragment key={row?.fromKid}></React.Fragment>
                                                                    ),
                                                                )}
                                                        </React.Fragment>
                                                    ) : (
                                                        <React.Fragment key={fromKid}></React.Fragment>
                                                    );
                                                })}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {showAlerts && (
                <SideDrawer
                    width="70%"
                    onClose={() => {
                        setShowAlerts(null);
                    }}
                >
                    <RelatedPartiesAlerts data={showAlerts} />
                </SideDrawer>
            )}
        </div>
    );

    if (isPDFReport) return isDataEmpty() ? <></> : <A4Page sectionName={REPORT_SECTION_NAMES.RELATED_PARTIES}>{render}</A4Page>;
    else return render;
}

export default RelatedParties;
